import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { ShieldAlert, AlertCircle, Loader2, LogOut } from "lucide-react";
import CustomerDashboard from "./dashboards/CustomerDashboard";
import PartnerDashboard from "./dashboards/PartnerDashboard";
import AdminDashboard from "./dashboards/AdminDashboard";
import SuperadminDashboard from "./dashboards/SuperadminDashboard";

function PartnerPendingScreen({ status }) {
  const { logout } = useAuth();
  const isRejected = status === "rejected";
  const isSuspended = status === "suspended";
  const headline = isRejected ? "Application Rejected"
    : isSuspended ? "Account Suspended"
    : "Pending Admin Approval";
  const body = isRejected
    ? "Your partner application was rejected by the admin team. Please contact support if you believe this is an error."
    : isSuspended
      ? "Your partner account has been suspended. Please contact support for details."
      : "Thanks for registering as a NakshaCart partner. An admin will review your profile and documents — typically within 24 hours. You'll be notified by email and WhatsApp once approved.";
  const Icon = isRejected || isSuspended ? AlertCircle : ShieldAlert;
  const accent = isRejected || isSuspended ? "rose" : "amber";

  return (
    <div className="bg-slate-50/40 min-h-[80vh] py-12" data-testid="partner-pending-screen">
      <div className="nc-container max-w-xl">
        <div className="bg-white rounded-2xl border border-slate-200 nc-soft-shadow p-7">
          <div className={`w-12 h-12 rounded-xl bg-${accent}-100 text-${accent}-700 flex items-center justify-center`}>
            <Icon className="w-6 h-6" />
          </div>
          <h2 className="mt-4 text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{headline}</h2>
          <p className="mt-3 text-slate-600 text-sm leading-relaxed">{body}</p>
          <p className="mt-4 text-xs text-slate-500">
            Current status: <strong className="capitalize">{status || "pending"}</strong>
          </p>
          <div className="mt-6">
            <button onClick={logout} className="nc-btn-secondary inline-flex items-center gap-2" data-testid="partner-pending-logout">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PartnerGate() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/partner/me")
      .then(({ data }) => { if (!cancelled) setProfile(data); })
      .catch(() => { if (!cancelled) setProfile({ status: "pending" }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (profile?.status !== "approved") {
    return <PartnerPendingScreen status={profile?.status} />;
  }
  return <PartnerDashboard />;
}

export default function Dashboard() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "superadmin") return <SuperadminDashboard />;
  if (user.role === "admin") return <AdminDashboard />;
  if (user.role === "partner") return <PartnerGate />;
  return <CustomerDashboard />;
}
